import React from 'react';
import PropTypes from 'prop-types';

import Select from '@decisiv/ui-components/lib/components/Select';
import TextField from '@decisiv/ui-components/lib/components/TextField';

import StepHeader from './StepHeader';
import { ADDITIONAL_STEP } from './copy';
import { POPOVER_Z_INDEX } from './layers';
import { FieldGroup, FieldPair, StepBody } from './styles';

/**
 * Step 3: optional contact details.
 *
 * Two behaviours here come from the designer's sticky notes rather than the
 * frames (Figma 787:76910 and 787:76920): State is disabled until a Country is
 * chosen, and changing the Country clears the State and shows a warning, because
 * the states offered depend on the country.
 *
 * Phone and Fax have no Country Code select, unlike Mobile on step 2. The mockup
 * shows one for each, but portal.users has only mobile_country_code — there is
 * nowhere to store a calling code for the other two.
 */
function AdditionalInfoStep({
  countryOptions,
  draft,
  isStateMissing,
  onChange,
  onChangeCountry,
  serverMessages,
  stateOptions,
}) {
  const handle = (field) => (event) => onChange(field, event.target.value);

  // Every field here can be rejected by the model even though the wizard asks
  // nothing of it, so each carries whatever the server said about it.
  const warn = (field) => ({
    warningMessage: serverMessages[field],
    withWarningIntent: Boolean(serverMessages[field]),
  });

  return (
    <StepBody>
      <StepHeader
        subtitle={ADDITIONAL_STEP.subtitle}
        title={ADDITIONAL_STEP.title}
      />
      <FieldGroup>
        <TextField
          label={ADDITIONAL_STEP.address1}
          name="address1"
          {...warn('address1')}
          value={draft.address1}
          onChange={handle('address1')}
        />
        <TextField
          label={ADDITIONAL_STEP.address2}
          name="address2"
          {...warn('address2')}
          value={draft.address2}
          onChange={handle('address2')}
        />
        <FieldPair>
          <Select
            label={ADDITIONAL_STEP.country}
            name="countryCode"
            options={countryOptions}
            value={draft.countryCode}
            {...warn('countryCode')}
            // Without this the dropdown renders under the wizard — see ./layers.
            zIndex={POPOVER_Z_INDEX}
            onChange={onChangeCountry}
          />
          <Select
            disabled={stateOptions.length === 0}
            label={ADDITIONAL_STEP.state}
            name="state"
            options={stateOptions}
            value={draft.state}
            warningMessage={
              serverMessages.state ||
              (isStateMissing ? ADDITIONAL_STEP.stateMissing : undefined)
            }
            withWarningIntent={isStateMissing || Boolean(serverMessages.state)}
            zIndex={POPOVER_Z_INDEX}
            onChange={(value) => onChange('state', value)}
          />
        </FieldPair>
        <FieldPair>
          <TextField
            label={ADDITIONAL_STEP.city}
            name="city"
            {...warn('city')}
            value={draft.city}
            onChange={handle('city')}
          />
          <TextField
            label={ADDITIONAL_STEP.postalCode}
            name="postalCode"
            {...warn('postalCode')}
            value={draft.postalCode}
            onChange={handle('postalCode')}
          />
        </FieldPair>
        <TextField
          label={ADDITIONAL_STEP.phone}
          name="phone"
          {...warn('phone')}
          type="tel"
          value={draft.phone}
          onChange={handle('phone')}
        />
        <TextField
          label={ADDITIONAL_STEP.fax}
          name="fax"
          {...warn('fax')}
          type="tel"
          value={draft.fax}
          onChange={handle('fax')}
        />
      </FieldGroup>
    </StepBody>
  );
}

const optionsShape = PropTypes.arrayOf(
  PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
);

AdditionalInfoStep.propTypes = {
  countryOptions: optionsShape,
  draft: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    countryCode: PropTypes.string,
    fax: PropTypes.string,
    phone: PropTypes.string,
    postalCode: PropTypes.string,
    state: PropTypes.string,
  }).isRequired,
  /** True once a Country with states is chosen but no State is. */
  isStateMissing: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onChangeCountry: PropTypes.func.isRequired,
  /** Field messages from a rejected create, keyed by draft field. */
  serverMessages: PropTypes.objectOf(PropTypes.string),
  /** Already narrowed to the chosen country; empty disables the select. */
  stateOptions: optionsShape,
};

AdditionalInfoStep.defaultProps = {
  countryOptions: [],
  isStateMissing: false,
  serverMessages: {},
  stateOptions: [],
};

export default AdditionalInfoStep;
